import React, { useEffect, useState } from 'react';
import Visibility from '@material-ui/icons/Visibility';
import { useLayoutContext } from '../../Context/Layout.context';
import { getPasswordPolicy, resetUserPassword } from '../../services/users/users.service';
import { AxiosError, AxiosResponse } from 'axios';
import _ from 'lodash';
import {
  BackendErrorCode,
  ErrorCode,
  FrontendResponseErrorCode,
} from '../../components/UserProfile/PasswordError.types';
import { useStyles } from '../../components/UserProfile/EditUserPassword.styles';
import {
  getErrorMessages,
  getPasswordPolicyMessage,
  validatePasswordWithPolicy,
} from '../../components/UserProfile/helpers';
import { ModalWithButtons } from '../../components/ModalWithButtons/ModalWithButtons.component';
import {
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from '@material-ui/core';
import { VisibilityOff } from '@material-ui/icons';
import { IPasswordPolicy, IUserProfile } from '../../components/UserProfile/IUserProfile.types';
import { useHistory } from 'react-router-dom';
import { IdentityClientErrors } from '../../services/users/users.types';
import { useTranslation } from 'react-i18next';

interface AddUserPasswordProps {
  user: IUserProfile | undefined;
  token: string;
  open: boolean;
}

export const AddUserPassword: React.FC<AddUserPasswordProps> = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();

  const [newPassword, setNewPassword] = useState<string>('');
  const [repeatedPassword, setRepeatedPassword] = useState<string>('');
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showRepeatedPassword, setShowRepeatedPassword] = useState<boolean>(false);
  const [errorCodes, setErrorCodes] = useState<ErrorCode[]>([]);
  const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState<boolean>(true);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);
  const [passwordPolicy, setPasswordPolicy] = useState<IPasswordPolicy | undefined>(undefined);

  const { openAlert, setIsLoader } = useLayoutContext();

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowRepeatedPassword = () => {
    setShowRepeatedPassword(!showRepeatedPassword);
  };

  const handleChangeNewPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value);
    setErrorCodes([]);
  };

  const handleChangeRepeatedPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRepeatedPassword(event.target.value);
    setErrorCodes([]);
  };

  const handleResetUserPassword = () => {
    setErrorCodes([]);
    setIsDataChanged(false);
    if (newPassword !== repeatedPassword) {
      setErrorCodes((oldArray: ErrorCode[]) => _.uniq([...oldArray, FrontendResponseErrorCode.PasswordsNotTheSame]));
    } else {
      setIsLoader(true);
      resetUserPassword(props.token, newPassword)
        .then(
          (res) => {
            if (res.data.succeeded) {
              openAlert(
                'success',
                <div style={{ display: 'inline' }}>
                  Dane zostały pomyślnie zapisane. Za chwilę nastąpi automatyczne przekierowanie do ekranu logowania.
                  Jeśli nie chcesz czekać, kliknij w <a href={'/'}>link</a>
                </div>,
              );
              setTimeout(() => {
                history.push('/');
              }, 20000);
            } else {
              openAlert(
                'error',
                <div style={{ display: 'inline' }}>
                  Wystąpił błąd podczas zapisywania danych. {res.data.errors[0].description}
                </div>,
              );
            }
            const validationErrorCodes = res.data.errors.map((error: IdentityClientErrors) => error.code as ErrorCode);
            setErrorCodes((oldArray: ErrorCode[]) => _.uniq([...oldArray, ...validationErrorCodes]));
          },
          (error: AxiosError) => {
            if (!navigator.onLine) {
              openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
            } else if (error.response?.data.errors) {
              openAlert('save_error');
              const validationErrorCodes = error.response?.data.errors.map((error: IdentityClientErrors) => error.code);
              setErrorCodes((oldArray: ErrorCode[]) => _.uniq([...oldArray, ...validationErrorCodes]));
            }
          },
        )
        .catch(() => {
          openAlert('save_error');
        })
        .finally(() => {
          setIsLoader(false);
        });
    }
  };

  useEffect(() => {
    setIsDataChanged(newPassword !== '' && repeatedPassword !== '');
    if (newPassword !== '' && repeatedPassword !== '' && newPassword !== repeatedPassword) {
      setErrorCodes((oldArray: ErrorCode[]) => _.uniq([...oldArray, FrontendResponseErrorCode.PasswordsNotTheSame]));
    }
    if (passwordPolicy && newPassword !== '' && repeatedPassword !== '' && newPassword === repeatedPassword) {
      if (!validatePasswordWithPolicy(newPassword, passwordPolicy)) {
        setErrorCodes((oldArray: ErrorCode[]) => _.uniq([...oldArray, FrontendResponseErrorCode.PasswordIsInvalid]));
      }
    }
  }, [newPassword, repeatedPassword, passwordPolicy]);

  useEffect(() => {
    setIsUpdateButtonDisabled(!isDataChanged || errorCodes.length > 0);
  }, [isDataChanged, errorCodes, errorCodes.length]);

  const oldPasswordIsValidButThereAreErrors =
    !errorCodes.includes(BackendErrorCode.PasswordMismatch) && errorCodes.length > 0;

  useEffect(() => {
    getPasswordPolicy().then((response: AxiosResponse<IPasswordPolicy>) => {
      setPasswordPolicy(response.data);
    });
  }, []);

  return (
    <>
      <ModalWithButtons
        open={props.open}
        size="sm"
        autoHeight={true}
        title={null}
        onConfirm={handleResetUserPassword}
        confirmButtonLabel="Zapisz"
        confirmButtonDisabled={isUpdateButtonDisabled}
        buttonsContainerStyle={{ marginTop: '20px' }}
        isHideBackdrop={true}
        annotations={
          <>
            <Grid item xs={12}>
              <hr className={classes.line}></hr>
            </Grid>
            <Grid item xs={12}>
              <div className={classes.footer}>{passwordPolicy && getPasswordPolicyMessage(passwordPolicy)}</div>
            </Grid>
          </>
        }
      >
        <Grid container className={classes.container}>
          <Grid>
            <div style={{ paddingTop: '35px' }} className={classes.modalSubTitle}>
              Ustaw nowe hasło w {t('global:shortNames.system')}
            </div>
          </Grid>

          <Grid item xs={12} className={classes.item}>
            <FormControl variant="outlined">
              <InputLabel
                style={{
                  color: oldPasswordIsValidButThereAreErrors ? '#BC0000' : '#2C2D3E',
                }}
                htmlFor="password"
              >
                Hasło
              </InputLabel>
              <OutlinedInput
                id="password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={handleChangeNewPassword}
                fullWidth={true}
                error={oldPasswordIsValidButThereAreErrors}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      disableRipple
                      aria-label="włącz widoczność hasła"
                      onClick={handleClickShowNewPassword}
                      edge="end"
                    >
                      {!showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                inputProps={{
                  style: {
                    width: '220px',
                    padding: '15.5px',
                    color: '#2C2D3E',
                    borderColor: oldPasswordIsValidButThereAreErrors ? '#BC0000' : '#2C2D3E',
                  },
                }}
                label="Hasło"
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} className={classes.item}>
            <FormControl variant="outlined">
              <InputLabel
                style={{
                  color: oldPasswordIsValidButThereAreErrors ? '#BC0000' : '#2C2D3E',
                }}
                htmlFor="repeted-password"
              >
                Powtórz hasło
              </InputLabel>
              <OutlinedInput
                id="repeted-password"
                type={showRepeatedPassword ? 'text' : 'password'}
                value={repeatedPassword}
                onChange={handleChangeRepeatedPassword}
                fullWidth={true}
                error={oldPasswordIsValidButThereAreErrors}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      disableRipple
                      aria-label="włącz widoczność hasła"
                      onClick={handleClickShowRepeatedPassword}
                      edge="end"
                    >
                      {!showRepeatedPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                inputProps={{
                  style: {
                    width: '220px',
                    padding: '15.5px',
                    color: '#2C2D3E',
                    borderColor: oldPasswordIsValidButThereAreErrors ? '#BC0000' : '#2C2D3E',
                  },
                }}
                label="Powtórz hasło"
              />
              <FormHelperText className={classes.errorMessagesContainer} error id="password-error" component={'div'}>
                {errorCodes.includes(FrontendResponseErrorCode.PasswordsNotTheSame) ? (
                  <div className={classes.errorMessage}>
                    {getErrorMessages(FrontendResponseErrorCode.PasswordsNotTheSame)}
                  </div>
                ) : (
                  <>
                    {!errorCodes.includes(BackendErrorCode.PasswordMismatch) &&
                      errorCodes.map((code: ErrorCode) => (
                        <div className={classes.errorMessage} key={code}>
                          {getErrorMessages(code)}
                        </div>
                      ))}
                  </>
                )}
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </ModalWithButtons>
    </>
  );
};
